// src/pages/Dashboard.tsx
import { useState, useEffect } from 'react';
import {
  Navbar,
  NavLeft,
  Block,
  BlockTitle,
  Card,
  CardContent,
  Button,
  Preloader,
  NavRight,
  f7,
} from 'framework7-react';
import { auth, getCurrentUser } from '../firebase';
import { signOut } from 'firebase/auth';
import '@styles/dashboard.css';
import { MainLayout } from '../layouts/MainLayout';

function Dashboard() {
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailSuccess, setEmailSuccess] = useState<string | null>(null);
  const [userName, setUserName] = useState<string>('');
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  useEffect(() => {
    getCurrentUser().then((user) => {
      if (user) {
        setUserName(user.email?.split('@')[0] || 'User');
      }
    });
  }, []);

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      setError(null);
      await signOut(auth);
      f7.views.main.router.navigate('/', { reloadAll: true });
    } catch {
      setError('Error logging out. Please try again.');
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleSendEmail = async () => {
    setIsSendingEmail(true);
    setError(null);
    setEmailSuccess(null);

    try {
      const user = await getCurrentUser();
      const response = await fetch('/api/sendEmail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: user?.email,
          subject: 'Test Email',
          text: 'This is a test email from your dashboard',
        }),
      });

      if (!response.ok) throw new Error('Failed to send email');

      f7.dialog.alert(
        `Thank you <strong>${userName}</strong>. We have received your request. A lawyer will respond within 1 hour.`,
        'Message'
      );
    } catch {
      setError('Error sending email. Please try again.');
    } finally {
      setIsSendingEmail(false);
    }
  };

  return (
    <MainLayout>
      <Navbar className="no-outline">
        <NavRight>👋 Hello, <b>{userName}</b>!</NavRight>
        <NavLeft>Welcome</NavLeft>
      </Navbar>

      <BlockTitle className="text-align-center">Dashboard</BlockTitle>

      <Block className="dashboard-block">
        <Card className="dashboard-card">
          <CardContent>
            <Button
              fill
              large
              onClick={() => f7.views.main.router.navigate('/videos')}
              className="margin-bottom"
              data-testid="btn-videos"
            >
              📚 Educational Videos
            </Button>

            {error && <Block strong inset className="color-red">{error}</Block>}
            {emailSuccess && <Block strong inset className="color-green">{emailSuccess}</Block>}

            <Button
              fill
              large
              onClick={handleSendEmail}
              disabled={isSendingEmail}
              className="margin-top"
              data-testid="btn-send-email"
            >
              {isSendingEmail ? (
                <>
                  <Preloader size={20} className="margin-right" />
                  Sending...
                </>
              ) : 'Send Test Email'}
            </Button>

            <Button
              fill
              large
              onClick={() => window.open('https://legalreview.ca', '_blank')}
              className="margin-top"
              data-testid="btn-legal-review"
            >
              Open Legal Review
            </Button>

            <Button
              fill
              large
              color="red"
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="margin-top"
              data-testid="btn-logout"
            >
              {isLoggingOut ? (
                <>
                  <Preloader size={20} className="margin-right" />
                  Logging out...
                </>
              ) : 'Log out'}
            </Button>
          </CardContent>
        </Card>
      </Block>
    </MainLayout>
  );
}

export default Dashboard;
